import {useEffect, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {initialValues} from './_module'
import clsx from 'clsx'
import Loader from 'app/custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import {useAppSelector, useAppDispatch} from '../../../../redux/hooks'
import {editRole, createRole, reset, getPermissions} from '../../../../redux/Slice/userSlice'
import {decryptId} from '_metronic/helpers/Encrypt'

const EditRole = () => {
  const {id} = useParams()
  const roleId = decryptId(id) || ''

  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<any[]>([])
  const [permissions, setPermission] = useState([])
  const {t} = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {errors} = useAppSelector((state) => state.user)

  useEffect(() => {
    dispatch(getPermissions()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setPermission(res.payload)
        setLoader(false)
      }
    })
    dispatch(editRole(roleId)).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setLoader(false)
      }
    })
  }, [])

  const userSchema = Yup.object().shape({
    name: Yup.string().required(
      t('VALIDATION.REQUIRED', {name: t('LABELS.NAME', {name: t('ROLES.ROLE')})})
    ),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: userSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      const formData = new FormData()
      formData.append('role_id', roleId)
      formData.append('name', values.name)
      selectedCheckboxes.forEach((item: any) => {
        formData.append('permission[]', item)
      })

      // Create New Role
      dispatch(createRole(formData)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          dispatch(reset())
          showSuccessMessage(t('MESSAGES.UPDATED'), () => {
            navigate(-1)
          })
        } else if (res.meta.requestStatus === 'rejected') {
          showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        }

        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all checkboxes
      const allCheckboxIds = permissions.map((data: any) => data.id)
      setSelectedCheckboxes(allCheckboxIds)
    } else {
      // Deselect all checkboxes
      setSelectedCheckboxes([])
    }
  }

  type CheckboxChangeEvent = React.ChangeEvent<HTMLInputElement>
  const handleCheckboxChange = (event: CheckboxChangeEvent, recordId: number) => {
    if (event.target.checked) {
      setSelectedCheckboxes([...selectedCheckboxes, recordId])
    } else {
      setSelectedCheckboxes(selectedCheckboxes.filter((id) => id !== recordId))
    }
  }

  return (
    <div className='card mb-5 mb-xl-10 mt-0'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder'>{t('BUTTONS.EDIT', {name: t('ROLES.ROLE')})}</h3>
        </div>
      </div>
      <div className='card-body p-9'>
        {!loader && (
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            autoComplete='off'
            onSubmit={formik.handleSubmit}
          >
            {/* First row */}
            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='col-lg-6 col-md-6 col-sm-12 mb-5'>
                <label className='col-form-label label fw-bolder required'>{t('LABEL.ROLE')}</label>
                <input
                  type='text'
                  placeholder={t('LABEL.ROLE')}
                  {...formik.getFieldProps('name')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': (formik.touched.name && formik.errors.name) || errors.name,
                    },
                    {
                      'is-valid': formik.touched.name && !formik.errors.name,
                    }
                  )}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block fw-bold'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.name}
                      </span>
                    </div>
                  </div>
                )}
                {errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block fw-bold'>
                      <span role='alert' className='text-danger'>
                        {t('VALIDATION.DUPLICATED RECORD', {
                          name: t('LABELS.NAME', {name: t('ROLES.ROLE')}),
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className='d-flex justify-content-between'>
                <h3 className='text-center'>{t('LABEL.PERMISSIONS')}</h3>
                {permissions.length > 0 && (
                  <label className='col-form-label label fw-bold'>
                    {t('GLOBAL.SELECT_ALL')}:
                    <input
                      className='form-check-input mx-3'
                      type='checkbox'
                      onChange={handleSelectAll}
                      checked={selectedCheckboxes.length === permissions.length}
                    />
                  </label>
                )}
              </div>
              <hr />

              {permissions.map((p: any) => {
                return (
                  <div className='form-check form-check-inline col-3 mx-13' key={p.id}>
                    <label className='form-check form-check-inline form-check-solid me-5'>
                      <input
                        {...formik.getFieldProps('permission')}
                        type='checkbox'
                        className='form-check-input mx-3'
                        id={p.id}
                        checked={selectedCheckboxes.includes(p.id)}
                        onChange={(event: CheckboxChangeEvent) => handleCheckboxChange(event, p.id)}
                      />
                      <span className='fw-bold ps-2 fs-6'>{p.name}</span>
                    </label>
                  </div>
                )
              })}

              {formik.touched.permission && formik.errors.permission && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block fw-bold'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.permission}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Second Row */}
            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='d-flex'>
                <div className='fs-6 fw-bold text-gray-700'>
                  <button
                    type='submit'
                    className='btn btn-sm btn-light btn-active-primary fw-bolder'
                    disabled={
                      formik.isSubmitting ||
                      !formik.isValid ||
                      loading ||
                      selectedCheckboxes.length === 0
                    }
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        <i className='fas fa-save mx-2'></i>
                        {t('BUTTONS.SAVE')}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {t('GLOBAL.LOADING')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  &nbsp;&nbsp;
                  <Link
                    className='btn btn-sm btn-flex btn-light btn-active-dark fw-bolder'
                    to='/roles-list'
                    onClick={() => dispatch(reset())}
                  >
                    <b>
                      <i className='fa-solid fa-circle-left mx-2'></i>
                      {t('BUTTONS.BACK')}
                    </b>
                  </Link>
                  <div />
                </div>
              </div>
            </div>
          </form>
        )}
        {loader && <Loader />}
      </div>
    </div>
  )
}

export default EditRole
